import React, { useState, useRef, useEffect } from "react";
import AuthModal from "./AuthModal";
import ProfileModal from "./ProfileModal";
import { useAuthStore } from "../store/authStore";
import { Coffee, Settings, LogOut, User as UserIcon } from "lucide-react"; 

// --- ASSETS ---
const smallLogo = new URL("../../logos/croped_Logo1_150x150.png", import.meta.url).href;
const loginImg = new URL('../assets/Login_Button/Login_Button_112x144.png', import.meta.url).href;

// Import all avatars
const avatarNB = new URL('../assets/Avatars/NonBinary_Druid_Avatar_224x288.png', import.meta.url).href;
const avatarMale = new URL('../assets/Avatars/Male_Druid_Avatar_224x288.png', import.meta.url).href;
const avatarFemale = new URL('../assets/Avatars/Female_Druid_Avatar_224x288.png', import.meta.url).href;

export default function Header({ navigateTo, handleSignOut, userGender }) {
  const { user } = useAuthStore();
  const isAuthed = !!user;
  
  const [showAuth, setShowAuth] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false); // <--- New State

  const profileMenuRef = useRef(null);

  const getAvatarImage = () => {
    // If the gender updates in settings, this will automatically reflect locally
    // assuming parent passes the updated userGender from store/props
    if (userGender === 'Male') return avatarMale;
    if (userGender === 'Female') return avatarFemale;
    return avatarNB; 
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinkClass = "bg-transparent border-0 px-3.5 py-2.5 font-bold text-base cursor-pointer rounded-lg transition-all duration-100 ease-out hover:-translate-y-[3px] hover:bg-black/[0.04]";
  const activeClass = "bg-[linear-gradient(180deg,rgba(93,92,255,0.08),rgba(93,92,255,0.04))]";
  const isCurrent = (path) => window.location.pathname === path;
  
  const interactiveImgClass = "block cursor-pointer transition-all duration-100 ease-[ease] leading-none hover:brightness-90 active:scale-95 active:brightness-100 bg-transparent border-0 p-0";

  const onSignOut = () => {
    handleSignOut();
    setShowProfileMenu(false);
  };

  return (
    <header className="relative flex h-24 w-full shrink-0 items-center justify-between bg-transparent pt-6 px-3 py-3 z-[2000]">
      <div className="absolute bottom-[-16px] left-3 right-3 h-px rounded-sm bg-slate-900/5" />      
      
      {/* LEFT: LOGO + NAV */}
      <div className="flex items-center gap-5">
        <button className="h-32 w-32 cursor-pointer border-0 bg-transparent p-0" onClick={() => navigateTo("/")}>
          <img src={smallLogo} className="h-full w-full object-contain" alt="GnosisFT"/>
        </button>
        <nav className="w-full hidden md:block">
          <ul className="flex list-none items-center gap-3 p-0 m-0">
            <li><button className={`${navLinkClass} ${isCurrent('/aboutus') ? activeClass : ''}`} onClick={() => navigateTo("/aboutus")}>About Us</button></li>
            <li><button className={`${navLinkClass} ${isCurrent('/news') ? activeClass : ''}`} onClick={() => navigateTo("/news")}>News</button></li>
            <li><button className={`${navLinkClass} ${isCurrent('/business') ? activeClass : ''}`} onClick={() => navigateTo("/business")}>Business</button></li>
            <li><button className={`${navLinkClass} ${isCurrent('/contactus') ? activeClass : ''}`} onClick={() => navigateTo("/contactus")}>Contact</button></li>
          </ul>
        </nav>
      </div>

      {/* RIGHT: COFFEE + USER */}
      <div className="ml-auto flex h-full items-center gap-5 pr-5">
       <a 
          href="https://www.buymeacoffee.com/gnosisft" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex h-[45px] w-[45px] items-center justify-center rounded-full border-2 border-white bg-[#FFDD00] shadow-[0_3px_6px_rgba(0,0,0,0.15)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_5px_10px_rgba(0,0,0,0.2)]"
          title="Buy me a coffee"
        >
          <Coffee size={24} color="#000" />
        </a>
        
        {!isAuthed ? (
          <button onClick={() => setShowAuth(true)} className={interactiveImgClass}>
            <img src={loginImg} alt="Login" className="h-[110px] w-auto"/>
          </button>
        ) : (
          <div className="relative" ref={profileMenuRef}>
            <button onClick={() => setShowProfileMenu(!showProfileMenu)} className={interactiveImgClass}>
              <img 
                src={getAvatarImage()}
                alt="User Profile" 
                className="h-[110px] w-auto rounded-full border-[3px] border-[#f0f0f0]"
              />
            </button>
            
            {showProfileMenu && (
              <div className="absolute right-0 top-full mt-[10px] w-[200px] rounded-xl border border-[#ddd] bg-white p-2 text-center shadow-2xl z-[1000] animate-[fadeIn_0.1s_ease-out]">
                <div className="px-2 py-2 mb-2 break-all text-xs text-[#888] border-b border-[#eee]">
                  {user.email}
                </div>
                
                {/* --- PROFILE SETTINGS LINK --- */}
                <button 
                  onClick={() => { setShowProfileSettings(true); setShowProfileMenu(false); }}
                  className="w-full flex items-center gap-3 cursor-pointer rounded-lg bg-transparent px-3 py-2 text-sm font-medium text-[#394A3C] hover:bg-[#f5f5f5] transition-colors"
                >
                   <UserIcon size={16} />
                   <span>Profile</span>
                </button>

                <button 
                  onClick={onSignOut} 
                  className="w-full flex items-center gap-3 cursor-pointer rounded-lg bg-transparent px-3 py-2 text-sm font-medium text-[#d93025] hover:bg-[#fff0f0] transition-colors mt-1"
                >
                   <LogOut size={16} />
                   <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Modals */}
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
        <ProfileModal open={showProfileSettings} onClose={() => setShowProfileSettings(false)} />
      </div>
    </header>
  );
}